import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Brain, Lightbulb, CheckCircle, ShieldAlert, Hash, Link, Eye, ThumbsUp, ThumbsDown, Globe, User, Bot, Archive, Tag, Search, X, Edit2, Trash2, AlertTriangle } from 'lucide-react';
import { useMemoriesStore } from '@/stores/agents/memories-store';
import { AgentMemory, memoriesApi } from '@/services/api/agentApi';
import { Button } from '@/components/ui/button';

// ─── Visual Editor Aspect Options ────────────────────────────────────────────

const ASPECT_FILTER_OPTIONS = [
  { value: '', label: 'All Aspects' },
  { value: 'visual-editing', label: 'Visual Editing' },
  { value: 'layout-knowledge', label: 'Layout Knowledge' },
  { value: 'style-knowledge', label: 'Style Knowledge' },
  { value: 'component-knowledge', label: 'Component Knowledge' },
  { value: 'user-correction', label: 'User Correction' },
  { value: 'data-visualization-mapping', label: 'Data Viz Mapping' },
] as const;

const getWeightColor = (weight: number): string => {
  if (weight > 0.7) return '#22c55e';  // green
  if (weight >= 0.3) return '#f59e0b'; // yellow/amber
  return '#ef4444';                     // red
};

const WeightBar: React.FC<{ weight: number }> = ({ weight }) => {
  const color = getWeightColor(weight);
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: `${weight * 100}%`,
            backgroundColor: color,
          }}
        />
      </div>
      <span
        className="text-xs w-10 text-right font-medium"
        style={{ color }}
      >
        {(weight * 100).toFixed(0)}%
      </span>
    </div>
  );
};

const OriginIcon: React.FC<{ origin?: string }> = ({ origin }) => {
  switch (origin) {
    case 'human': return <User className="h-3 w-3" />;
    case 'web': return <Globe className="h-3 w-3" />;
    default: return <Bot className="h-3 w-3" />;
  }
};

const MemoryCard: React.FC<{
  memory: AgentMemory;
  onCorrect?: (id: string, text: string) => void;
  onDelete?: (id: string) => void;
}> = ({ memory, onCorrect, onDelete }) => {
  const [correcting, setCorrecting] = useState(false);
  const [correctionText, setCorrectionText] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const timeAgo = (date: string) => {
    const diff = Date.now() - new Date(date).getTime();
    const hours = Math.floor(diff / 3600000);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  const isHypothesis = memory.type === 'hypothesis';
  const isDirective = memory.type === 'directive';
  const isCorrectionType = memory.type === 'correction';
  const isLegacyCorrection = memory.aspects.includes('user-correction');

  const typeBadgeClass = isCorrectionType
    ? 'bg-orange-500/10 text-orange-600'
    : isDirective
      ? 'bg-red-500/10 text-red-600'
      : isHypothesis
        ? 'bg-amber-500/10 text-amber-600'
        : 'bg-emerald-500/10 text-emerald-600';

  const TypeIcon = isCorrectionType ? ShieldAlert : isDirective ? ShieldAlert : isHypothesis ? Lightbulb : CheckCircle;

  // Card border: correction type or legacy corrections get amber/orange highlight, directives get red
  const cardBorderClass = (isCorrectionType || isLegacyCorrection)
    ? 'border-amber-500/50 bg-amber-500/5'
    : isDirective
      ? 'border-red-500/30 bg-red-500/5'
      : memory.archived
        ? 'border-border opacity-60'
        : 'border-border';

  return (
    <div className={`border rounded-lg p-4 space-y-3 ${cardBorderClass}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm flex-1">{memory.text}</p>
        <div className="flex items-center gap-1.5 shrink-0">
          {/* Origin badge */}
          {memory.origin && (
            <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground">
              <OriginIcon origin={memory.origin} />
              {memory.origin}
            </span>
          )}
          {/* Archived badge */}
          {memory.archived && (
            <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground">
              <Archive className="h-3 w-3" />
            </span>
          )}
          {/* Type badge */}
          <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${typeBadgeClass}`}>
            <TypeIcon className="h-3 w-3" />
            {memory.type}
          </span>
        </div>
      </div>

      {/* Supersedes link for corrections */}
      {memory.supersedes && (
        <p className="text-xs text-orange-600">
          Corrects: <span className="font-mono">{memory.supersedes.slice(0, 12)}...</span>
        </p>
      )}
      {memory.superseded_by && (
        <p className="text-xs text-muted-foreground line-through">
          Superseded by: <span className="font-mono">{memory.superseded_by.slice(0, 12)}...</span>
        </p>
      )}

      <WeightBar weight={memory.weight} />

      {/* Category + Aspects */}
      <div className="flex flex-wrap gap-1">
        {memory.category && (
          <span className="flex items-center gap-0.5 text-xs px-2 py-0.5 rounded bg-blue-500/10 text-blue-600">
            <Tag className="h-2.5 w-2.5" />
            {memory.category}
          </span>
        )}
        {memory.aspects.length > 0 && memory.aspects.map((aspect, i) => {
          const isUserCorrection = aspect === 'user-correction';
          return (
            <span
              key={i}
              className={`flex items-center gap-0.5 text-xs px-2 py-0.5 rounded ${
                isUserCorrection
                  ? 'bg-amber-500/15 text-amber-700'
                  : 'bg-secondary'
              }`}
            >
              <Hash className="h-2.5 w-2.5" />
              {aspect}
            </span>
          );
        })}
      </div>

      {/* Web sources */}
      {memory.sources && memory.sources.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {memory.sources.map((source, i) => (
            <a
              key={i}
              href={source.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-0.5 text-xs px-2 py-0.5 rounded bg-blue-500/10 text-blue-600 hover:bg-blue-500/20 transition-colors"
              title={`${source.authority} source`}
            >
              <Globe className="h-2.5 w-2.5" />
              {source.title || new URL(source.url).hostname}
            </a>
          ))}
        </div>
      )}

      {/* Stats row: use_count + feedback_score + meta */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1 font-medium">
            <Eye className="h-3 w-3" />
            {memory.use_count}x used
          </span>
          <span>Last: {timeAgo(memory.last_used)}</span>
          {memory.feedback_score !== 0 && (
            <span className={`flex items-center gap-1 font-medium ${memory.feedback_score > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
              {memory.feedback_score > 0
                ? <ThumbsUp className="h-3 w-3" />
                : <ThumbsDown className="h-3 w-3" />}
              {memory.feedback_score > 0 ? '+' : ''}{memory.feedback_score.toFixed(1)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {memory.event_anchor && (
            <span className="flex items-center gap-1">
              <Link className="h-3 w-3" />
              Anchored
            </span>
          )}
          {onCorrect && (
            <button
              onClick={() => { setCorrecting(!correcting); setCorrectionText(''); setConfirmDelete(false); }}
              className="flex items-center gap-1 text-blue-600 hover:text-blue-700"
            >
              <Edit2 className="h-3 w-3" />
              Correct
            </button>
          )}
          {onDelete && (
            <button
              onClick={() => { setConfirmDelete(!confirmDelete); setCorrecting(false); }}
              className="flex items-center gap-1 text-red-500 hover:text-red-600"
            >
              <Trash2 className="h-3 w-3" />
              Delete
            </button>
          )}
        </div>
      </div>

      {/* Correction input */}
      {correcting && onCorrect && (
        <div className="flex items-center gap-2 pt-1">
          <input
            type="text"
            placeholder="Enter the correct information..."
            value={correctionText}
            onChange={(e) => setCorrectionText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && correctionText.trim()) {
                onCorrect(memory._id, correctionText);
                setCorrecting(false);
                setCorrectionText('');
              }
            }}
            className="flex-1 px-3 py-1.5 text-sm border border-border rounded bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
            autoFocus
          />
          <Button
            size="sm"
            onClick={() => {
              if (correctionText.trim()) {
                onCorrect(memory._id, correctionText);
                setCorrecting(false);
                setCorrectionText('');
              }
            }}
            disabled={!correctionText.trim()}
          >
            Submit
          </Button>
        </div>
      )}

      {/* Delete confirmation */}
      {confirmDelete && onDelete && (
        <div className="flex items-center gap-2 p-2 bg-red-500/5 border border-red-500/20 rounded">
          <AlertTriangle className="h-4 w-4 text-red-500 shrink-0" />
          <span className="text-xs text-red-600 flex-1">Permanently delete this memory?</span>
          <Button size="sm" variant="destructive" onClick={() => { onDelete(memory._id); setConfirmDelete(false); }}>
            Delete
          </Button>
        </div>
      )}
    </div>
  );
};

export const MemoriesTab: React.FC = () => {
  const { memories, total, isLoading, error, fetchMemories, searchMemories, searchQuery, setSearchQuery, filters, setFilters } = useMemoriesStore();
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    fetchMemories();
  }, []);

  // Compute health summary — must be before early returns to avoid hooks violation
  const healthSummary = useMemo(() => {
    if (memories.length === 0) return null;
    const totalWeight = memories.reduce((sum, m) => sum + m.weight, 0);
    const avgWeight = totalWeight / memories.length;
    const highWeight = memories.filter((m) => m.weight > 0.7).length;
    const lowWeight = memories.filter((m) => m.weight < 0.3).length;
    const corrections = memories.filter((m) => m.type === 'correction' || m.aspects.includes('user-correction')).length;
    const totalFeedback = memories.reduce((sum, m) => sum + m.feedback_score, 0);
    return { avgWeight, highWeight, lowWeight, corrections, totalFeedback };
  }, [memories]);

  if (isLoading && memories.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-muted-foreground">Loading memories...</div>
      </div>
    );
  }

  if (error && memories.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center space-y-2">
          <p className="text-destructive">{error}</p>
          <button onClick={() => fetchMemories()} className="text-sm text-primary hover:underline">Retry</button>
        </div>
      </div>
    );
  }

  if (memories.length === 0) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <div className="text-center space-y-4">
          <Brain className="h-16 w-16 mx-auto text-muted-foreground/40" />
          <h3 className="text-lg font-medium text-muted-foreground">No Memories Yet</h3>
          <p className="text-sm text-muted-foreground/70 max-w-md">
            Memories will accumulate as agents analyze data and generate insights.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-6">
      {/* Search Bar */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search memories (semantic + keyword)..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && searchInput.trim()) {
                searchMemories(searchInput.trim());
              }
            }}
            className="w-full pl-10 pr-20 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {searchQuery && (
              <button
                onClick={() => {
                  setSearchInput('');
                  setSearchQuery('');
                  fetchMemories();
                }}
                className="p-1 text-muted-foreground hover:text-foreground rounded"
                title="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
            <button
              onClick={() => {
                if (searchInput.trim()) {
                  searchMemories(searchInput.trim());
                }
              }}
              disabled={!searchInput.trim()}
              className="px-2 py-1 text-xs bg-primary text-primary-foreground rounded disabled:opacity-40"
            >
              Search
            </button>
          </div>
        </div>
        {searchQuery && (
          <p className="text-xs text-muted-foreground mt-1">
            Showing results for &ldquo;{searchQuery}&rdquo; &mdash;{' '}
            <button
              onClick={() => { setSearchInput(''); setSearchQuery(''); fetchMemories(); }}
              className="text-primary hover:underline"
            >
              clear search
            </button>
          </p>
        )}
      </div>

      {/* Header + Filters */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold">{total} Memor{total !== 1 ? 'ies' : 'y'}</h2>
          <p className="text-sm text-muted-foreground">
            {searchQuery ? `Search results` : `Sorted by ${filters.sortBy || 'weight'}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Aspect filter */}
          <select
            className="text-sm border border-border rounded px-2 py-1 bg-background"
            value={(filters.aspects && filters.aspects.length > 0) ? filters.aspects[0] : ''}
            onChange={(e) => {
              const aspect = e.target.value;
              setFilters({
                ...filters,
                aspects: aspect ? [aspect] : undefined,
              });
              fetchMemories();
            }}
          >
            {ASPECT_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          {/* Type filter */}
          <select
            className="text-sm border border-border rounded px-2 py-1 bg-background"
            value={filters.type || ''}
            onChange={(e) => {
              setFilters({ ...filters, type: (e.target.value || undefined) as any });
              fetchMemories();
            }}
          >
            <option value="">All Types</option>
            <option value="hypothesis">Hypothesis</option>
            <option value="fact">Fact</option>
            <option value="directive">Directive</option>
            <option value="correction">Correction</option>
          </select>

          {/* Origin filter */}
          <select
            className="text-sm border border-border rounded px-2 py-1 bg-background"
            value={filters.origin || ''}
            onChange={(e) => {
              setFilters({ ...filters, origin: (e.target.value || undefined) as any });
              fetchMemories();
            }}
          >
            <option value="">All Origins</option>
            <option value="agent">Agent</option>
            <option value="human">Human</option>
            <option value="web">Web</option>
          </select>

          {/* Archived toggle */}
          <label className="flex items-center gap-1.5 text-sm text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={filters.archived || false}
              onChange={(e) => {
                setFilters({ ...filters, archived: e.target.checked || undefined });
                fetchMemories();
              }}
              className="rounded border-border"
            />
            Archived
          </label>

          {/* Sort */}
          <select
            className="text-sm border border-border rounded px-2 py-1 bg-background"
            value={filters.sortBy || 'weight'}
            onChange={(e) => {
              setFilters({ ...filters, sortBy: e.target.value });
              fetchMemories();
            }}
          >
            <option value="weight">Weight</option>
            <option value="last_used">Last Used</option>
            <option value="use_count">Use Count</option>
            <option value="created_at">Created</option>
          </select>
        </div>
      </div>

      {/* Health summary bar */}
      {healthSummary && (
        <div className="flex items-center gap-4 mb-4 p-3 bg-muted/30 rounded-lg border border-border text-xs">
          <div className="flex items-center gap-1.5">
            <span className="text-muted-foreground">Avg Weight:</span>
            <span className="font-medium" style={{ color: getWeightColor(healthSummary.avgWeight) }}>
              {(healthSummary.avgWeight * 100).toFixed(0)}%
            </span>
          </div>
          <div className="w-px h-4 bg-border" />
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-green-500" />
            <span className="text-muted-foreground">High:</span>
            <span className="font-medium">{healthSummary.highWeight}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-red-500" />
            <span className="text-muted-foreground">Low:</span>
            <span className="font-medium">{healthSummary.lowWeight}</span>
          </div>
          {healthSummary.corrections > 0 && (
            <>
              <div className="w-px h-4 bg-border" />
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-amber-500" />
                <span className="text-muted-foreground">Corrections:</span>
                <span className="font-medium text-amber-700">{healthSummary.corrections}</span>
              </div>
            </>
          )}
          <div className="w-px h-4 bg-border" />
          <div className="flex items-center gap-1.5">
            <span className="text-muted-foreground">Net Feedback:</span>
            <span className={`font-medium ${healthSummary.totalFeedback >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
              {healthSummary.totalFeedback > 0 ? '+' : ''}{healthSummary.totalFeedback.toFixed(1)}
            </span>
          </div>
        </div>
      )}

      {/* Memory list */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {memories.map((memory) => (
          <MemoryCard
            key={memory._id}
            memory={memory}
            onCorrect={async (id, text) => {
              try {
                await memoriesApi.submitFeedback({
                  thread_id: 'manual-correction',
                  message_id: id,
                  signal: 'negative',
                  correction_text: text,
                  memory_ids: [id],
                });
                fetchMemories();
              } catch { /* silent */ }
            }}
            onDelete={async (id) => {
              try {
                await memoriesApi.delete(id);
                fetchMemories();
              } catch { /* silent */ }
            }}
          />
        ))}
      </div>
    </div>
  );
};
